import {
	type AIProviderName,
	type AISettingsPublic,
	getHostBridge,
	isEmbedded,
} from "@emkit/adapter-emdash/bridge";
import { useEffect, useState } from "react";
import { useEditor } from "../store.js";

const PROVIDERS: { value: AIProviderName; label: string }[] = [
	{ value: "openai", label: "OpenAI (ChatGPT)" },
	{ value: "gemini", label: "Google Gemini" },
	{ value: "grok", label: "xAI Grok" },
	{ value: "claude", label: "Anthropic Claude" },
];

/**
 * Curated model lists per provider. The first entry is the default for
 * that provider (selected when the user switches provider). The list is
 * intentionally short — we don't try to enumerate every preview/snapshot
 * model, since pricing and naming change weekly. The "Custom…" sentinel
 * at the end of every list lets advanced users paste an exact model id
 * when they need something specific (preview models, dated snapshots,
 * region-pinned variants).
 */
interface ModelChoice {
	value: string;
	label: string;
}

const CUSTOM_SENTINEL = "__custom__";

const MODELS_BY_PROVIDER: Record<AIProviderName, ModelChoice[]> = {
	openai: [
		{ value: "gpt-5", label: "GPT-5 (latest, reasoning)" },
		{ value: "gpt-5-mini", label: "GPT-5 Mini (fast, cheap)" },
		{ value: "gpt-4.1", label: "GPT-4.1" },
		{ value: "gpt-4o", label: "GPT-4o" },
		{ value: "gpt-4o-mini", label: "GPT-4o Mini" },
	],
	gemini: [
		{ value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
		{ value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
		{ value: "gemini-2.0-flash", label: "Gemini 2.0 Flash" },
		{ value: "gemini-2.0-flash-lite", label: "Gemini 2.0 Flash Lite (cheapest)" },
	],
	grok: [
		{ value: "grok-4", label: "Grok 4" },
		{ value: "grok-4-fast", label: "Grok 4 Fast" },
		{ value: "grok-3", label: "Grok 3" },
		{ value: "grok-3-mini", label: "Grok 3 Mini" },
	],
	claude: [
		{ value: "claude-opus-4-7", label: "Claude Opus 4.7 (most capable)" },
		{ value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (balanced)" },
		{ value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (fast)" },
	],
};

const DEFAULT_MODELS: Record<AIProviderName, string> = {
	openai: MODELS_BY_PROVIDER.openai[0]?.value ?? "gpt-5",
	gemini: MODELS_BY_PROVIDER.gemini[2]?.value ?? "gemini-2.0-flash",
	grok: MODELS_BY_PROVIDER.grok[0]?.value ?? "grok-4",
	claude: MODELS_BY_PROVIDER.claude[2]?.value ?? "claude-haiku-4-5-20251001",
};

function isPresetModel(provider: AIProviderName, model: string): boolean {
	return MODELS_BY_PROVIDER[provider].some((m) => m.value === model);
}

/**
 * Modal for configuring the AI provider that powers the prompt box.
 *
 * Server-side only: keys are stored in the plugin's KV (host CMS) and
 * never come back to the editor — the GET response only carries
 * `hasKey: boolean`. Editor in standalone mode (no host) shows a
 * disabled state because there's nowhere to put the key.
 */
export function AISettings() {
	const isOpen = useEditor((s) => s.isAISettingsOpen);
	const close = useEditor((s) => s.closeAISettings);
	const embedded = isEmbedded();

	const [loaded, setLoaded] = useState<AISettingsPublic | null>(null);
	const [provider, setProvider] = useState<AIProviderName>("openai");
	const [model, setModel] = useState<string>(DEFAULT_MODELS.openai);
	const [customModel, setCustomModel] = useState(false);
	const [apiKey, setApiKey] = useState<string>("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [savedFlash, setSavedFlash] = useState(false);

	useEffect(() => {
		if (!isOpen || !embedded) return;
		let cancelled = false;
		getHostBridge()
			.getAISettings()
			.then((s) => {
				if (cancelled) return;
				setLoaded(s);
				if (s.provider) {
					setProvider(s.provider);
					const m = s.model ?? DEFAULT_MODELS[s.provider];
					setModel(m);
					// If the stored model isn't in the curated list (preview or
					// dated snapshot the user pasted), open the custom-input
					// state so the field still shows what's actually saved.
					setCustomModel(!isPresetModel(s.provider, m));
				}
				// apiKey field stays empty — we never see the stored key.
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof Error ? err.message : "Failed to load settings");
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, embedded]);

	const onProviderChange = (next: AIProviderName) => {
		setProvider(next);
		// Auto-suggest the provider's default model so a user doesn't paste
		// an OpenAI model name into Gemini and vice-versa.
		setModel(DEFAULT_MODELS[next]);
		setCustomModel(false);
	};

	const onModelSelect = (next: string) => {
		if (next === CUSTOM_SENTINEL) {
			// Switch to custom-input mode but keep the current model value
			// so the user has a starting point to edit rather than blank.
			setCustomModel(true);
			return;
		}
		setModel(next);
	};

	const save = async () => {
		setSaving(true);
		setError(null);
		try {
			const result = await getHostBridge().setAISettings({
				provider,
				model,
				apiKey, // Empty string keeps the existing key when changing model/provider.
			});
			setLoaded(result);
			setApiKey("");
			setSavedFlash(true);
			setTimeout(() => setSavedFlash(false), 1800);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Save failed");
		} finally {
			setSaving(false);
		}
	};

	if (!isOpen) return null;

	const canSave = !saving && (apiKey.length > 0 || loaded?.hasKey);

	return (
		<div
			className="emke-modal-backdrop"
			role="dialog"
			aria-modal="true"
			aria-label="AI settings"
			onClick={(e) => {
				if (e.target === e.currentTarget) close();
			}}
		>
			<div className="emke-modal" style={{ maxWidth: 520 }}>
				<header className="emke-modal-header">
					<div>
						<div
							style={{
								fontSize: 11,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								color: "var(--fg-dim)",
							}}
						>
							AI
						</div>
						<h2 style={{ margin: "4px 0 0 0", fontSize: 18 }}>AI Settings</h2>
					</div>
					<button
						type="button"
						className="emke-btn"
						onClick={close}
						aria-label="Close AI settings"
					>
						✕
					</button>
				</header>

				<div className="emke-modal-body" style={{ padding: "8px 0 4px" }}>
					{!embedded ? (
						<div style={{ padding: 16, fontSize: 13, lineHeight: 1.6, color: "var(--fg-dim)" }}>
							AI features are only available when EmBuilder is opened from
							inside EmDash CMS. Open the editor from your site's admin to
							configure a provider.
						</div>
					) : (
						<>
							<div className="emke-inspector-field">
								<label>Provider</label>
								<select
									value={provider}
									onChange={(e) => onProviderChange(e.target.value as AIProviderName)}
									style={{ width: "100%" }}
								>
									{PROVIDERS.map((p) => (
										<option key={p.value} value={p.value}>
											{p.label}
										</option>
									))}
								</select>
							</div>

							<div className="emke-inspector-field">
								<label>Model</label>
								{customModel ? (
									<div style={{ display: "flex", gap: 6 }}>
										<input
											type="text"
											value={model}
											onChange={(e) => setModel(e.target.value)}
											placeholder={DEFAULT_MODELS[provider]}
											style={{ flex: 1, minWidth: 0 }}
											autoFocus
										/>
										<button
											type="button"
											className="emke-btn"
											onClick={() => {
												setCustomModel(false);
												setModel(DEFAULT_MODELS[provider]);
											}}
											title="Back to preset list"
											style={{ padding: "4px 10px", fontSize: 11 }}
										>
											Presets
										</button>
									</div>
								) : (
									<select
										value={
											isPresetModel(provider, model) ? model : DEFAULT_MODELS[provider]
										}
										onChange={(e) => onModelSelect(e.target.value)}
										style={{ width: "100%" }}
									>
										{MODELS_BY_PROVIDER[provider].map((m) => (
											<option key={m.value} value={m.value}>
												{m.label}
											</option>
										))}
										<option value={CUSTOM_SENTINEL}>Custom… (paste your own)</option>
									</select>
								)}
							</div>

							<div className="emke-inspector-field">
								<label>
									API key{" "}
									{loaded?.hasKey && (
										<span style={{ color: "var(--accent)", fontWeight: 400 }}>
											· already set, leave blank to keep
										</span>
									)}
								</label>
								<input
									type="password"
									value={apiKey}
									onChange={(e) => setApiKey(e.target.value)}
									placeholder={loaded?.hasKey ? "•••••••• (leave blank to keep)" : "Paste your key"}
									style={{ width: "100%" }}
									autoComplete="off"
								/>
							</div>

							<div
								style={{
									padding: "0 16px 12px",
									fontSize: 11,
									color: "var(--fg-dim)",
									lineHeight: 1.6,
								}}
							>
								Your key is stored on your CMS server (never in the browser) and
								is used by the plugin to call the provider on your behalf.
								You're billed by the provider directly.
							</div>

							{error && (
								<div
									style={{
										margin: "4px 16px 8px",
										padding: 10,
										fontSize: 12,
										color: "var(--danger, #b91c1c)",
										background: "rgba(185, 28, 28, 0.08)",
										borderRadius: 6,
									}}
								>
									{error}
								</div>
							)}

							{savedFlash && (
								<div
									style={{
										margin: "4px 16px 8px",
										padding: 10,
										fontSize: 12,
										color: "var(--accent, #16a34a)",
										background: "rgba(34, 197, 94, 0.1)",
										borderRadius: 6,
									}}
								>
									Settings saved.
								</div>
							)}
						</>
					)}
				</div>

				<footer className="emke-modal-footer">
					{embedded ? (
						<>
							<span style={{ color: "var(--fg-dim)", fontSize: 12 }}>
								{loaded?.hasKey ? "Configured" : "Not yet configured"}
							</span>
							<button
								type="button"
								className="emke-btn is-primary"
								onClick={save}
								disabled={!canSave}
							>
								{saving ? "Saving…" : "Save"}
							</button>
						</>
					) : (
						<button type="button" className="emke-btn" onClick={close}>
							Close
						</button>
					)}
				</footer>
			</div>
		</div>
	);
}
